import { ClientAvatar } from '@app/components/base/clients/ClientAvatar';
import { Client, User, UserRole } from '@mi-front/core';
import {
  Box,
  Chip,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemProps,
  ListItemText,
  Typography,
} from '@mui/material';

interface Props extends ListItemProps {
  readonly user: User & { client: Client };
  readonly button?: boolean;
}

/**
 * List item component that displays a user with client avatar, client name, email, and role chip.
 * Used in impersonation screens for displaying user options.
 * When button prop is true, uses ListItemButton instead of ListItem for clickable behavior.
 * Children can be used to add secondary actions (e.g., delete button).
 */
export const ImpersonationUserListItem = (props: Props) => {
  const { user, button = false, children, ...listItemProps } = props;
  const ListItemComponent = (
    button ? ListItemButton : ListItem
  ) as React.ComponentType<ListItemProps>;

  return (
    <ListItemComponent {...listItemProps}>
      <ListItemAvatar>
        {user.client && <ClientAvatar client={user.client} size={24} />}
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            {user.client?.name}
            {user.role === UserRole.CLIENT_ADMIN && (
              <Chip
                color="info"
                label={user.role.split('_').join(' ')}
                size="small"
                sx={{ fontSize: 10, height: 20 }}
              />
            )}
          </Box>
        }
        secondary={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 0.5,
            }}
          >
            {user.email}
            <Typography variant="caption" color="text.secondary">
              {user.name} {user.surname}
            </Typography>
          </Box>
        }
        primaryTypographyProps={{
          fontSize: 12,
        }}
        secondaryTypographyProps={{
          component: 'div',
          fontSize: 16,
        }}
      />
      {children}
    </ListItemComponent>
  );
};
